import React, { useMemo, useRef, useState } from 'react';
import { useI18n } from '../useI18n.js';
import { Avatar } from './Avatar';
import { MessageBody } from './MessageBody';
import { computeTickDodge } from '../tickLayout';

function fmtDate(ts) {
  try { return new Date(ts).toLocaleDateString('fr-FR', { day: '2-digit', month: 'short', year: 'numeric' }); }
  catch { return ''; }
}

/**
 * Mode "chrono" : deviner QUAND un message a ete poste. Le joueur glisse un curseur
 * sur une frise (periode du dataset) puis valide ; score = proximite. Reveal : le
 * marqueur de la vraie date apparait, les crans se retournent sur les points gagnes.
 */
export function ChronoRound({
  phase, message, messages = [], period, timeLeft,
  hasGuessed, onGuess, guessTicks = [],
  answerFraction, proposals = {}, roundPoints = {}, myPseudo,
}) {
  const { t } = useI18n();
  const barRef = useRef(null);
  const [cursor, setCursor] = useState(0.5); // fraction 0..1
  const [dragging, setDragging] = useState(false);
  const [namesUp, setNamesUp] = useState(false);

  const reveal = phase === 'result' || phase === 'transition';
  const min = period?.min ?? 0;
  const max = period?.max ?? (min + 1);
  const span = Math.max(1, max - min);
  const cursorTs = Math.round(min + cursor * span);
  const actualTs = answerFraction != null ? Math.round(min + answerFraction * span) : null;

  // Remontee des pseudos apres le flip (comme le mode audio).
  React.useEffect(() => {
    if (!reveal) { setNamesUp(false); return; }
    const to = setTimeout(() => setNamesUp(true), 650);
    return () => clearTimeout(to);
  }, [reveal]);

  const fromEvent = (e) => {
    const el = barRef.current;
    if (!el) return;
    const r = el.getBoundingClientRect();
    const f = Math.max(0, Math.min(1, (e.clientX - r.left) / Math.max(1, r.width)));
    setCursor(f);
  };
  const canPlace = phase === 'thinking' && !hasGuessed && !reveal;
  const onDown = (e) => { if (!canPlace) return; setDragging(true); fromEvent(e); try { e.currentTarget.setPointerCapture(e.pointerId); } catch { /* ignore */ } };
  const onMove = (e) => { if (dragging && canPlace) fromEvent(e); };
  const onUp = () => setDragging(false);
  const lock = () => { if (canPlace) onGuess(cursorTs); };

  const pointsColor = (p) => (p >= 70 ? 'good' : p >= 30 ? 'mid' : 'bad');
  // Anti-collision des crans (meme helper que MashupRound).
  const tickDodge = useMemo(() => computeTickDodge(guessTicks), [guessTicks]);
  const scaleLabels = useMemo(() => [min, min + span / 2, max].map(fmtDate), [min, max, span]);

  // Crans a chaque debut de mois (repere de periode) ; label d'annee en janvier.
  const monthMarks = useMemo(() => {
    if (!(max > min)) return [];
    const marks = [];
    const d = new Date(min);
    d.setDate(1); d.setHours(0, 0, 0, 0);
    if (d.getTime() < min) d.setMonth(d.getMonth() + 1);
    let guard = 0;
    while (d.getTime() <= max && guard++ < 800) {
      const ts = d.getTime();
      marks.push({ ts, fraction: (ts - min) / span, isYear: d.getMonth() === 0, year: d.getFullYear() });
      d.setMonth(d.getMonth() + 1);
    }
    return marks;
  }, [min, max, span]);

  return (
    <div className="chrono-round">
      {/* Discussion d'une journee a dater (scrollable, echelle reduite) */}
      <div className="chrono-convo">
        {(messages || []).map((m, i) => {
          const author = (m && typeof m === 'object') ? m.author : null;
          const avatarUrl = (m && typeof m === 'object') ? m.avatarUrl : null;
          const text = (m && typeof m === 'object') ? m.text : m;
          return (
            <div key={i} className="chrono-cmsg">
              <Avatar url={avatarUrl} label={author || '?'} className="chrono-cmsg-av" size={22} />
              <div className="chrono-cmsg-body">
                {author && <span className="chrono-cmsg-author">{author}</span>}
                <span className="chrono-cmsg-text"><MessageBody content={text} imgClassName="chrono-img" /></span>
              </div>
            </div>
          );
        })}
      </div>

      <div className="chrono-topline">
        <span className="chrono-hint">{reveal ? (actualTs != null ? `${t('chronoActualDate') || 'Date reelle'} : ${fmtDate(actualTs)}` : '') : (hasGuessed ? (t('chronoWaiting') || 'En attente des autres joueurs...') : t('chronoDragHint'))}</span>
        {phase === 'thinking' && timeLeft > 0 && <span className="chrono-timer">{timeLeft}s</span>}
      </div>

      <div className="chrono-scale-labels">
        <span>{scaleLabels[0]}</span><span>{scaleLabels[1]}</span><span>{scaleLabels[2]}</span>
      </div>

      <div
        ref={barRef}
        className={`mashup-bar chrono-bar${canPlace ? ' placeable' : ''}`}
        onPointerDown={onDown}
        onPointerMove={onMove}
        onPointerUp={onUp}
      >
        {/* Crans de mois (repere temporel) */}
        {monthMarks.map((mk, i) => (
          <div key={i} className={`chrono-month${mk.isYear ? ' year' : ''}`} style={{ left: `${mk.fraction * 100}%` }}>
            {mk.isYear && <span className="chrono-month-lbl">{mk.year}</span>}
          </div>
        ))}
        {/* Curseur mobile (avant validation) */}
        {canPlace && (
          <div className="chrono-cursor" style={{ left: `${cursor * 100}%` }}>
            <span className="chrono-cursor-date">{fmtDate(cursorTs)}</span>
          </div>
        )}
        {/* Marqueur de la vraie date (reveal) */}
        {reveal && actualTs != null && (
          <div className="chrono-answer" style={{ left: `${answerFraction * 100}%` }}>
            <span className="chrono-answer-flag">{fmtDate(actualTs)}</span>
          </div>
        )}
        {/* Crans des joueurs : masques tant que tout le monde n'a pas devine
            (avant reveal on ne montre que le sien) */}
        {(reveal ? guessTicks : guessTicks.filter((g) => g.pseudo === myPseudo)).map((g) => {
          const pts = roundPoints[g.pseudo] || 0;
          return (
            <div key={g.pseudo} className="mashup-tick" style={{ left: `${g.fraction * 100}%`, transform: `translateX(calc(-50% + ${tickDodge[g.pseudo] || 0}px))` }}>
              <div className={`mashup-tick-name${namesUp ? ' up' : ''}`}>{g.pseudo}</div>
              <div className={`mashup-tick-flip${reveal ? ' revealed' : ''}`}>
                <div className="mashup-tick-face front">
                  <Avatar url={g.avatarUrl} label={g.pseudo} className="mashup-tick-img" size={34} />
                </div>
                <div className={`mashup-tick-face back pts pts-${pointsColor(pts)}`}>{pts}</div>
              </div>
              <div className="mashup-tick-stem" />
            </div>
          );
        })}
      </div>

      {phase === 'thinking' && !hasGuessed && (
        <button type="button" className="btn primary chrono-lock" onClick={lock}>{t('chronoLock') || 'Valider'}</button>
      )}
    </div>
  );
}
